import React, {
  createContext, useContext, useState, useCallback, useEffect, ReactNode,
} from 'react';
import { LANGUAGES, type LangCode, type Translations } from './translations';
import type { KeyValueStorage } from '@shootout-comps/core';

const STORAGE_KEY = 'ss_lang';

interface LangContextValue {
  lang: LangCode;
  t: Translations;
  setLang: (code: LangCode) => void;
}

const LangContext = createContext<LangContextValue | null>(null);

export interface LanguageProviderProps {
  children: ReactNode;
  storage: KeyValueStorage;
  // Returns the device/browser's preferred language tag (e.g. 'en-US'), used to
  // auto-detect a starting language when none has been stored yet.
  detectLang?: () => string | undefined;
  // Fired whenever the active language changes, so each platform can apply its
  // own RTL/layout-direction mechanism (DOM attributes on web, I18nManager on native).
  onLangChange?: (lang: LangCode, dir: 'ltr' | 'rtl') => void;
}

export function LanguageProvider({ children, storage, detectLang, onLangChange }: LanguageProviderProps) {
  // Starts as 'en' and resolves the real stored/detected language asynchronously —
  // storage reads are always async under the KeyValueStorage abstraction (unlike the
  // old web-only localStorage-backed lazy initializer), so there's one extra render
  // with the default language before the real one applies.
  const [lang, setLangState] = useState<LangCode>('en');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const stored = await storage.getItem(STORAGE_KEY) as LangCode | null;
      if (stored && LANGUAGES[stored]) {
        if (!cancelled) setLangState(stored);
        return;
      }
      const detected = detectLang?.()?.split('-')[0] as LangCode | undefined;
      if (detected && LANGUAGES[detected] && !cancelled) setLangState(detected);
    })();
    return () => { cancelled = true; };
  }, [storage, detectLang]);

  const setLang = useCallback((code: LangCode) => {
    void storage.setItem(STORAGE_KEY, code);
    setLangState(code);
  }, [storage]);

  useEffect(() => {
    onLangChange?.(lang, LANGUAGES[lang].dir);
  }, [lang, onLangChange]);

  return (
    <LangContext.Provider value={{ lang, t: LANGUAGES[lang], setLang }}>
      {children}
    </LangContext.Provider>
  );
}

export function useLang(): LangContextValue {
  const ctx = useContext(LangContext);
  if (!ctx) throw new Error('useLang must be used within LanguageProvider');
  return ctx;
}
